import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';

const Ctx = createContext(null);

/**
 * App-level provider that owns ALL download state + navigation guard.
 * Wrap your <App> (inside <BrowserRouter>) with <DownloadProvider>.
 */
export function DownloadProvider({ children }) {
  const [states, setStates] = useState({});
  const controllersRef = useRef({});

  const hasActive = Object.values(states).some((s) => s.status === 'downloading');

  // ── Navigation guard ──
  const [blocked, setBlocked] = useState(false);
  const pendingNavRef = useRef(null);
  const allowNavRef = useRef(false);
  const originalPushRef = useRef(null);

  useEffect(() => {
    if (!hasActive) {
      if (originalPushRef.current) {
        window.history.pushState = originalPushRef.current;
        originalPushRef.current = null;
      }
      return;
    }

    const originalPush = window.history.pushState.bind(window.history);
    originalPushRef.current = originalPush;

    window.history.pushState = function (...args) {
      if (allowNavRef.current) {
        allowNavRef.current = false;
        return originalPush(...args);
      }
      pendingNavRef.current = args;
      setBlocked(true);
    };

    const handlePopState = () => {
      originalPush(null, '', window.location.href);
      setBlocked(true);
      pendingNavRef.current = null;
    };

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.history.pushState = originalPush;
      originalPushRef.current = null;
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasActive]);

  const proceedNavigation = useCallback(() => {
    setBlocked(false);
    const args = pendingNavRef.current;
    pendingNavRef.current = null;

    // Abort every active download
    Object.keys(controllersRef.current).forEach((id) => {
      controllersRef.current[id]?.abort();
      delete controllersRef.current[id];
    });
    setStates({});

    if (args) {
      allowNavRef.current = true;
      window.history.pushState(...args);
    } else {
      window.history.back();
    }
  }, []);

  const cancelNavigation = useCallback(() => {
    setBlocked(false);
    pendingNavRef.current = null;
  }, []);

  // ── Download logic ──
  const update = useCallback((id, patch) => {
    setStates((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || { status: 'idle', progress: 0, downloaded: 0, total: 0 }), ...patch },
    }));
  }, []);

  const startDownload = useCallback(async (id, getSignedUrl, fileName, hintBytes) => {
    if (controllersRef.current[id]) return;

    const ctrl = new AbortController();
    controllersRef.current[id] = ctrl;
    update(id, { status: 'downloading', progress: 0, downloaded: 0, total: hintBytes || 0 });

    try {
      const signedUrl = await getSignedUrl();
      if (!signedUrl) throw new Error('No download URL');

      const resp = await fetch(signedUrl, { signal: ctrl.signal });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const contentLength = parseInt(resp.headers.get('content-length') || '0', 10);
      const total = contentLength || hintBytes || 0;
      if (total) update(id, { total });

      const reader = resp.body?.getReader();
      if (!reader) {
        const blob = await resp.blob();
        save(blob, fileName);
        done(id, blob.size, blob.size, fileName);
        return;
      }

      const chunks = [];
      let downloaded = 0;
      while (true) {
        const { done: eof, value } = await reader.read();
        if (eof) break;
        chunks.push(value);
        downloaded += value.length;
        const progress = total ? Math.min(Math.round((downloaded / total) * 100), 100) : 0;
        update(id, { downloaded, progress, total: total || 0 });
      }

      const blob = new Blob(chunks);
      save(blob, fileName);
      done(id, downloaded, total || downloaded, fileName);
    } catch (err) {
      if (err.name === 'AbortError') {
        update(id, { status: 'idle', progress: 0, downloaded: 0, total: 0 });
        toast.info('Download cancelled');
      } else {
        update(id, { status: 'error', progress: 0 });
        toast.error('Download failed');
      }
    } finally {
      delete controllersRef.current[id];
    }

    function done(dlId, dl, tot, fName) {
      update(dlId, { status: 'done', progress: 100, downloaded: dl, total: tot });
      toast.success(`Downloaded: ${fName}`);
      setTimeout(() => {
        setStates((prev) => {
          if (prev[dlId]?.status === 'done') {
            const next = { ...prev };
            delete next[dlId];
            return next;
          }
          return prev;
        });
      }, 4000);
    }
  }, [update]);

  const cancelDownload = useCallback((id) => {
    controllersRef.current[id]?.abort();
  }, []);

  const getState = useCallback(
    (id) => states[id] || { status: 'idle', progress: 0, downloaded: 0, total: 0 },
    [states]
  );

  const value = {
    startDownload,
    cancelDownload,
    getState,
    blocked,
    proceedNavigation,
    cancelNavigation,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Consume the global download context from any component. */
export function useDownload() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useDownload must be used within <DownloadProvider>');
  return ctx;
}

function save(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || 'document';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
}

/** Format bytes to human-readable string */
export function formatBytes(bytes) {
  if (bytes === null || bytes === undefined) return 'Unknown';
  if (bytes <= 0) return 'Unknown';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}
